import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import MaterialReactTable, { type MRT_ColumnDef } from "material-react-table";
import {
  SiteByIdDTO,
  UserDTO,
  deActivateEstimateById,
  deleteCollectorById,
  deleteDataProvider,
  deleteSiteById,
  getSiteById,
  getUnitCostByCollectorId,
  updateCollector,
} from "api";

import {
  EsgDeleteIcon,
  EsgNotesIcon,
  EsgRecIcon,
  EsgTruck,
  PlusIcon,
} from "assets";

import {
  COLORS,
  DeleteDialog,
  EMPTY_CELL_VALUE,
  useDialogControls,
} from "components";

import {
  useDeActivatedCollectorsBySiteId,
  useDeActivatedEstimatesBySiteId,
  useNotify,
} from "hooks";

import { QUERY_KEYS } from "consts";
import { useUserRole } from "recoils";

import {
  getDateFormat,
  isNullOrUndefined,
  toUSNumber,
  getLoggedUserData
} from "utils";

import { SITES_PAGE } from "../../consts";

import { CollectorModal } from "./components/CollectorModal";
import { EstimatesModal } from "./components/EstimatesModal";
import UnitCostInfoDrawer from "./components/UnitCostInfoDrawer";
import { UserModal } from "./components/UserModal";
import { AllowedFormValues } from "./types";

import { SiteNameDetail } from "../site-delete-dialog-name";
import { SwitchToActivation } from "../switch-to-activation";
import { collectorsColumns } from "./consts";

export const SiteEditorForm: FunctionComponent<{
  allowedUsers: UserDTO[];
  allowedValues: AllowedFormValues;
  site?: SiteByIdDTO;
  toDeleteSite: boolean;
}> = ({ allowedValues, site, allowedUsers, toDeleteSite }) => {

  // ---- Hooks ----
  const notify = useNotify();
  const queryClient = useQueryClient();
  const userRole = useUserRole();
  const navigate = useNavigate();
  const deleteDialogControls = useDialogControls();
  const deleteDataProviderControls = useDialogControls();
  const loggedUserData = getLoggedUserData();

  // ---- State ----
  const [addCollectorModalOpen, setAddCollectorModalOpen] = useState(false);
  const [addEstimatesModalOpen, setAddEstimatesModalOpen] = useState(false);
  const [EditCollector, setEditCollector] = useState<any | undefined>();
  const [EditCollectorOpen, setEditCollectorOpen] = useState(false);
  const [clickType, setClickType] = useState<string>("");
  const [EditEstimate, setEditEstimate] = useState<any | undefined>();
  const [EditEstimateOpen, setEditEstimateOpen] = useState(false);
  const [editEstimateNotesOpen, setEditEstimateNotesOpen] = useState(false);
  const [collectorData, setCollectorData] = useState<any | undefined>();
  const [UnitCostData, setUnitCostData] = useState<any | undefined>();
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [deleteEstimateId, setDeleteEstimateId] = useState<number | undefined>(
    undefined,
  );
  const [isEstimateModalOpen, setEstimateModalOpen] = useState(false);
  const [isEditNotes, setIsEditNotes] = useState(false);
  const [isCollectorModalOpen, setCollectorModalOpen] = useState(false);
  const [isCollectorEditNotes, setIsCollectorEditNotes] = useState(false);
  const [isEditUnitCost, setIsEditUnitCost] = useState(false);
  const [showSideInfoDrawer, setShowSideInfoDrawer] = useState(false);
  const [historicData, setHistoricData] = useState<any>(null);
  const [siteData, setSiteData] = useState<SiteByIdDTO | undefined>(site);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [dataProviderId, setDataProviderId] = useState(0);
  const [deActivatedCollectorsList, setDeActivatedCollectorsList] = useState([]);
  const [deActivatedEstimatesList, setDeActivatedEstimatesList] = useState([]);
  const [sureToDeleteCollectorsModal, setSureToDeleteCollectorsModal] = useState<boolean>(false);
  const [currentCollectorToDelete, setCurrentCollectorToDelete] = useState<any>(null);

  // ---- API Calls ----
  const { mutateAsync: deleteSiteRequest } = useMutation(deleteSiteById);

  const useDeleteDataProvider = () => {
    const mutationFn = ({ siteId, userId }: { siteId: number; userId: number }) =>
      deleteDataProvider(siteId, userId);

    return useMutation(mutationFn, {
      onSuccess: () => {
        notify.success('Data provider successfully added!');
        queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      },
      onError: () => {
        notify.error('Some error has occurred while adding the data provider!');
      },
    });
  };

  const { mutate: deleteDataProviderRequest } = useDeleteDataProvider();

  useEffect(() => {
    setSiteData(site);
  }, [site]);

  const onCollectorDataUpdate = (data: any) => {
    setSiteData(data);
  };

  const onEstimatesDataUpdate = (data: any) => {
    setSiteData(data);
  };

  const { mutateAsync: updateCollectorRequest } = useMutation(updateCollector);

  useEffect(() => {
    if (siteData) window.localStorage.setItem("Site", JSON.stringify(siteData));
  }, [siteData]);

  useEffect(() => {
    const fetchData = async () => {
      if (UnitCostData) {
        try {
          const data = await getUnitCostByCollectorId(UnitCostData?.id);
          setHistoricData(data);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
    };

    fetchData();
  }, [UnitCostData]);

  const { mutateAsync: deleteCollectorRequest } =
    useMutation(deleteCollectorById);

  const { mutateAsync: deleteEstimateRequest } = useMutation(
    deActivateEstimateById,
  );

  const {
    data: DeActivatedcollectorsFromApi,
    refetch: refetchCollectors,
  } = useDeActivatedCollectorsBySiteId(siteData?.id ?? null);

  const {
    data: DeActivatedEstimatesFromApi,
    refetch: refetchEstimates,
  } = useDeActivatedEstimatesBySiteId(siteData?.id ?? null);

  useEffect(() => {
    if (siteData?.id) {
      // Refetch data when siteData.id changes
      refetchCollectors();
      refetchEstimates();
    }
  }, [siteData?.id, refetchCollectors, refetchEstimates]);

  useEffect(() => {
    if (DeActivatedcollectorsFromApi) {
      setDeActivatedCollectorsList(DeActivatedcollectorsFromApi as any);
    }
  }, [DeActivatedcollectorsFromApi]);

  useEffect(() => {
    if (DeActivatedEstimatesFromApi) {
      setDeActivatedEstimatesList(DeActivatedEstimatesFromApi as any);
    }
  }, [DeActivatedEstimatesFromApi]);

  const handleCloseCollectorModal = () => {
    const isAddCollectorModalOpen = addCollectorModalOpen;
    const isEditCollectorOpen = EditCollectorOpen;
    refetchCollectors()
    refetchEstimates()
    if (isAddCollectorModalOpen) {
      setAddCollectorModalOpen(false);
    }
    if (isEditCollectorOpen) {
      setEditCollectorOpen(false);
    }
    if (isCollectorEditNotes) {
      setCollectorModalOpen(false);
      setIsCollectorEditNotes(false);
    }

    setEditCollector(undefined);
  };

  const handleEditCollector = (rowData: any, type: string) => {
    setClickType(type)
    setEditCollector(rowData);
    setEditCollectorOpen(true);
  };

  const handleEditNotesInCollector = (rowData: any) => {
    setCollectorModalOpen(true);
    setIsCollectorEditNotes(true);
    setCollectorData(rowData);
  };

  const handleEditEstimates = (rowData: any) => {
    setEditEstimateOpen(true);
    setEditEstimate(rowData);
  };

  const handleEditEstimatesNotes = (rowData: any) => {
    setEditEstimateNotesOpen(true);
    setIsEditNotes(true);
    setEditEstimate(rowData);
  };


  const handleCloseEstimatesModal = () => {
    const isAddEstiamtesModalOpen = addEstimatesModalOpen;
    const isEditEstimateOpen = EditEstimateOpen;

    if (isAddEstiamtesModalOpen) {
      setAddEstimatesModalOpen(false);
    }
    if (isEditEstimateOpen) {
      setEditEstimateOpen(false);
    }
    if (editEstimateNotesOpen) {
      setEditEstimateNotesOpen(false);
      setIsEditNotes(false);
    }
    if (isEstimateModalOpen) {
      setEstimateModalOpen(false);
      setIsEditNotes(false);
    }

    setEditEstimate(undefined);
  };

  const setEstimateToDelete = (id: number | undefined) => {
    deleteDialogControls.open();
    if (id) {
      setDeleteEstimateId(id);
    }
  };

  const deleteCollector = (id: number | undefined) => {
    onDisableCollector(id);
    deleteDialogControls.close();
  };

  const deleteEstimate = (id: number | undefined) => {
    onDisableEstimate(id);
    deleteDialogControls.close();
  };

  const handleDeleteProvider = async (deleteUserId: number | undefined) => {
    if (siteData && deleteUserId) {
      const selectedDataProviderId = deleteUserId;

      deleteDataProviderRequest(
        {
          siteId: siteData?.id,
          userId: selectedDataProviderId,
        },
        {
          onSuccess: () => {
            deleteDataProviderControls.close();
          },
        },
      );
    }
  };

  const onDisableCollector = async (collectorId: number | undefined) => {
    if (siteData?.id && collectorId) {
      await deleteCollectorRequest(collectorId, {
        onError: () => {
          deleteDialogControls.close();
          notify.error(`Some error has happen while deleting Collector!`);
        },
        onSuccess: () => {
          deleteDialogControls.close();
          notify.success(`Collector successfully deleted!`);
          queryClient.invalidateQueries([QUERY_KEYS.SITES]);
          queryClient.invalidateQueries([
            QUERY_KEYS.COLLECTORS,
            QUERY_KEYS.SITES,
            site?.id,
          ]);
          if (site) {
            getSiteById(site?.id)
              .then((siteDetails) => {
                setSiteData(siteDetails);
              })
              .catch((error) => {
                // eslint-disable-next-line
                console.error("Error fetching site data:", error);
              });
          }
        },
      });
    }
  };

  const onDisableEstimate = async (EstimateId: number | undefined) => {
    if (siteData?.id && EstimateId) {
      await deleteEstimateRequest(EstimateId, {
        onError: () => {
          deleteDialogControls.close();
          notify.error(`Some error has happen while de-activating Estimate!`);
        },
        onSuccess: () => {
          deleteDialogControls.close();
          notify.success(`Estimate successfully de-activated!`);
          queryClient.invalidateQueries([QUERY_KEYS.SITES]);
          queryClient.invalidateQueries([
            QUERY_KEYS.ESTIMATES,
            QUERY_KEYS.SITES,
            site?.id,
          ]);
          if (site) {
            getSiteById(site?.id)
              .then((siteDetails) => {
                setSiteData(siteDetails);
              })
              .catch((error) => {
                // eslint-disable-next-line
                console.error("Error fetching site data:", error);
              });
          }
        },
      });
    }
  };

  const onDeleteClick = async () => {
    if (siteData?.id) {
      try {
        await deleteSiteRequest(siteData.id, {
          onSuccess: () => {
            deleteDialogControls.close();
            notify.success(`Site successfully deleted: ${siteData.name}!`);
            queryClient.invalidateQueries([QUERY_KEYS.SITES]);
            navigate(SITES_PAGE.ABSOLUTE_PATH);
            window.sessionStorage.removeItem('expanded-sites');
          },
        });
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : `Some error has happen while deleting site: ${siteData.name}!`);
        deleteDialogControls.close();
      }
    }
  };

  const dataProvidersColumns = useMemo(
    () => [
      {
        accessorKey: "firstName",
        header: "First Name",
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
      },
      {
        accessorKey: "email",
        header: "Email Address",
      },
    ],
    [],
  ) as MRT_ColumnDef<any>[];

  const generatedCollectorsColumns: MRT_ColumnDef<any>[] = collectorsColumns(
    siteData || ({} as SiteByIdDTO),
    updateCollectorRequest,
    notify,
    setIsEditUnitCost,
    isEditUnitCost,
    setShowSideInfoDrawer,
    setUnitCostData,
    UnitCostData,
    setOpenSnackBar,
    openSnackBar,
    setSiteData,
  );

  const EstimatesColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "estimateName",
      header: "Estimate Name",
    },
    {
      accessorKey: "utilityName",
      header: "Utility Type",
    },
    {
      accessorKey: "estimateType",
      header: "Estimate Type",
    },
    {
      accessorKey: "estimateHeadCount",
      header: "Estimation Value",
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;
        return (
          <Tooltip title={value}>
            <Box>{toUSNumber(value)}</Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "estimateUsage",
      header: "Estimated Usage per Day",
      Header: <Box>Estimated Usage per Day</Box>,
      size: 240,
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;
        return (
          <Tooltip title={value}>
            <Box>{toUSNumber(value)}</Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "estimateUsageUnit",
      header: "Consumption Unit",
    },
    {
      accessorKey: "estimateStartDate",
      header: "Start Date",
      accessorFn: (row) =>
        dayjs(row.estimateStartDate).utc().format(getDateFormat()),
    },
    {
      accessorKey: "estimateEndDate",
      header: "End Date",
      accessorFn: (row) =>
        dayjs(row.estimateEndDate).utc().format(getDateFormat()),
    },
    {
      accessorKey: "scope",
      header: "GHG Emissions",
    },
    {
      accessorKey: "estimateSource",
      header: "Estimate Source",
      Cell: ({ cell }: any) => {
        const value = cell.getValue() as string;

        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }

        return (
          <Tooltip title={value}>
            <Box>{value}</Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "notes",
      header: "Notes",
    },
  ];

  const collectorRecords = JSON.parse(
    JSON.stringify(siteData?.collectors || []),
  );

  const userRecords = JSON.parse(JSON.stringify(siteData?.users || []));

  const estimatesRecords = JSON.parse(
    JSON.stringify(siteData?.estimates || []),
  );

  const handleREC = (collector: any, siteId: any) => {
    if (collector && collector.id && siteId) {
      navigate(`/rec/${collector.id}?s=${siteId}`, { state: { collector } });
    } else {
      notify.error("Some error has occurred while opening selected REC");
    }
  };

  const handleUtilitySpecificEmissionsFactors = (collectorId: any, siteId: any) => {
    navigate(`/utility-specific-emissions-factors/${collectorId}?s=${siteId}`);
  };

  const showRecIcon = (row: any) => {
    if (row?.recsDetail === null) {
      return <EsgRecIcon color="grey" />;
    }

    return <EsgRecIcon color={row?.recsDetail?.recsUploaded ? "green" : "red"} />;
  };

  const showEmissionFactorIcon = (row: any) => {
    const isDocumentUploaded = row?.emissionFactorDetails?.some((t: any) => t.documentUploaded === true)
    if (row?.emissionFactorDetails?.length === 0) {
      return <EsgTruck color="grey" />;
    }

    return <EsgTruck color={isDocumentUploaded ? COLORS.Mainblue : "red"} />;
  };

  const handlerFullyDeleteCollector = async (collector: any) => {
    const collectorId = collector.id;
    if (siteData?.id && collectorId) {
      try {
        await deleteCollectorRequest(collectorId, {

          onSuccess: () => {
            notify.success(`Collector successfully deleted!`);
            setSureToDeleteCollectorsModal(false);
            queryClient.invalidateQueries([QUERY_KEYS.SITES]);
            queryClient.invalidateQueries([
              QUERY_KEYS.COLLECTORS,
              QUERY_KEYS.SITES,
              site?.id,
            ]);
          },
        })
      } catch (error: any) {
        notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : "This collector has associated bills. Please remove the bills first");
        setSureToDeleteCollectorsModal(false);
      }
    }
  };

  const handlerOpenCollectorDeleteModal = async (collector: any) => {
    setCurrentCollectorToDelete(collector);
    setSureToDeleteCollectorsModal(true);
  };

  return (
    <>
      <Divider sx={{ mt: "45px", borderColor: "#d9d9d9" }} />
      <Box sx={{
        px: 3,
        mt: 1,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <p style={{ fontSize: "16px", fontWeight: "500" }}>
            Parameter Based Estimations
          </p>
          <Button
            sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
            variant="contained"
            onClick={() => setAddEstimatesModalOpen(true)}
            startIcon={<PlusIcon />}
            disabled={userRole.isAuditor}
          >
            Add Estimates
          </Button>
        </Box>
        <Box>
          <MaterialReactTable
            muiTableContainerProps={{
              sx: {
                "& thead": {
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
                "& thead tr th:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                  pr: 3,
                  "& > div": {
                    pl: "8px",
                  },
                },
                "& tbody tr td:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                },
                "& thead tr th:nth-of-type(2)": {
                  pl: 3,
                },
                "& tbody tr td:nth-of-type(2)": {
                  pl: 3,
                },
                border: "1px solid #D9D9D9",
                borderRadius: "10px",
                minWidth: "100%",
                width: "100%",
                maxWidth: "100%",
                "@media (min-width: 600px)": {
                  width: "100%",
                },
                "@media (min-width: 960px)": {
                  width: "1100px",
                },
                "@media (min-width: 1280px)": {
                  width: "1100px",
                },
                "@media (min-width: 1440px)": {
                  width: "1100px",
                },
                "@media (min-width: 1680px)": {
                  width: "1300px",
                },
                "@media (min-width: 2400px)": {
                  width: "100%",
                },
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                color: COLORS.Mainblue,
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                height: "45px",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => {
                if (row.original.enabled) {
                  handleEditEstimates(row.original)
                }
              },
              sx: {
                cursor: row.original.enabled ? "pointer" : "auto",
                height: "45px",
                backgroundColor: "white",
              },
            })}
            columns={EstimatesColumns}
            data={estimatesRecords.concat(deActivatedEstimatesList)}
            enablePagination={false}
            enableDensityToggle={false}
            initialState={{
              density: "compact",
            }}
            enableRowActions
            renderRowActions={({ row }) => (
              <Box
                sx={{
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  pl: "5px",
                }}
              >
                <SwitchToActivation
                  row={row.original}
                  textOn="Deactivate"
                  textOff="De-Activated"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.original?.enabled) {
                      setEstimateToDelete(row.original.id as number);
                    }
                  }}
                />
                <Tooltip arrow placement="top" title="Notes">
                  <IconButton
                    sx={{ width: "35px", height: "35px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.original?.enabled) {
                        handleEditEstimatesNotes(row.original);
                      }
                    }}
                  >
                    <EsgNotesIcon color={row.original.notes?.length > 0 ? COLORS.Romance : 'grey'} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Box>
      </Box>
      <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
      <Box sx={{
        px: 3,
        mt: 1,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <p style={{ fontSize: "16px", fontWeight: "500" }}>
            Utility Providers
          </p>
          <Button
            sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
            variant="contained"
            onClick={() => setAddCollectorModalOpen(true)}
            startIcon={<PlusIcon />}
            disabled={userRole.isAuditor}
          >
            Add Utility Provider
          </Button>
        </Box>
        <Box>
          <MaterialReactTable
            muiTableContainerProps={{
              sx: {
                "& thead": {
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                },
                "& thead tr th:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                  pr: 3,
                  "& > div": {
                    pl: "8px",
                  },
                },
                "& tbody tr td:first-of-type": {
                  borderRight: "1px solid #e0e0e0",
                  pr: 0,
                },
                "& thead tr th:nth-of-type(2)": {
                  pl: 3,
                },
                "& tbody tr td:nth-of-type(2)": {
                  pl: 3,
                },
                border: "1px solid #D9D9D9",
                borderRadius: "10px",
                minWidth: "100%",
                width: "100%",
                maxWidth: "100%",
                "@media (min-width: 600px)": {
                  width: "100%",
                },
                "@media (min-width: 960px)": {
                  width: "1100px",
                },
                "@media (min-width: 1280px)": {
                  width: "1100px",
                },
                "@media (min-width: 1440px)": {
                  width: "1100px",
                },
                "@media (min-width: 1680px)": {
                  width: "1300px",
                },
                "@media (min-width: 2400px)": {
                  width: "100%",
                },
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                color: COLORS.Mainblue,
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                height: "45px",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleEditCollector(row.original, ""),
              sx: {
                cursor: "pointer",
                height: "45px",
                backgroundColor: "white",
              },
            })}
            columns={generatedCollectorsColumns}
            data={[
              ...collectorRecords,
              ...deActivatedCollectorsList.map((collector: any) => ({
                ...collector,
                isDeactivated: true,
              })),
            ].sort((a, b) => {
              if (a.disableDate === null && b.disableDate !== null) {
                return -1;
              }
              if (a.disableDate !== null && b.disableDate === null) {
                return 1;
              }
              return 0;
            })}
            enablePagination={false}
            enableDensityToggle={false}
            initialState={{
              density: "compact",
            }}
            enableRowActions
            renderRowActions={({ row }) => (
              <div
                style={{
                  color: row.original.isDeactivated ? "red" : "",
                  borderRight: "1px solid rgba(224,224,224,1)",
                  height: "45px",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                  paddingRight: "5px",
                }}
              >
                <Box
                  sx={{
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    px: "5px",
                  }}
                >
                  <SwitchToActivation
                    row={row.original}
                    textOn="Deactivate"
                    textOff="Activate"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (site?.enabled) {
                        handleEditCollector(row.original, row.original?.enabled ? "Disable" : "Enable")
                      } else {
                        notify.error("You cannot reactivate a collector belonging to a deactivated site")
                      }
                    }}
                  />
                  <Tooltip arrow placement="top" title="Notes">
                    <IconButton
                      sx={{ width: "36px", height: "36px", padding: "4px" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditNotesInCollector(row.original)
                      }}
                    >
                      <EsgNotesIcon color={row.original.enabled && row.original.notes?.length > 0 ? COLORS.Romance : 'grey'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    arrow
                    placement="top"
                    title={row?.original?.recsDetail === null
                      ? 'Upload REC'
                      : row?.original?.recsDetail?.recsUploaded === true
                        ? 'REC is Uploaded'
                        : 'REC document missing'}
                  >
                    <IconButton
                      sx={{ width: "36px", height: "36px", padding: "4px" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleREC(row.original, siteData?.id)
                      }}
                    >
                      {showRecIcon(row?.original)}
                    </IconButton>
                  </Tooltip>
                  {/* Utility Specific Emissions Factors */}
                  <Tooltip
                    arrow
                    placement="top"
                    title="Utility Provider-specific EF"
                  >
                    <IconButton
                      sx={{ width: "36px", height: "36px", padding: "4px" }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUtilitySpecificEmissionsFactors(row.original?.id, siteData?.id)
                      }}
                    >
                      {showEmissionFactorIcon(row?.original)}
                    </IconButton>
                  </Tooltip>
                  {/* Delete collector (Only Super Admin) or deactivate collector */}
                  {loggedUserData.superadmin
                    ? <Tooltip arrow placement="top" title="Delete">
                      <IconButton
                        sx={{ width: "36px", height: "36px", padding: "4px" }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handlerOpenCollectorDeleteModal(row.original)
                        }}
                      >
                        <EsgDeleteIcon color="#EF2E14" />
                      </IconButton>
                    </Tooltip>
                    : null}
                </Box>
              </div >
            )}
          />
        </Box>
      </Box>
      <Divider sx={{ mt: 3, borderColor: "#d9d9d9" }} />
      <Box sx={{
        px: 3,
        mt: 1,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
          <p style={{ fontSize: "16px", fontWeight: "500" }}>
            Data Providers
          </p>
          <Button
            sx={{ borderRadius: "8px", width: "200px", height: "36px" }}
            variant="contained"
            onClick={() => setAddUserModalOpen(true)}
            startIcon={<PlusIcon />}
            disabled={userRole.isAuditor || userRole.isDataProvider}
          >
            Add Data Provider
          </Button>
        </Box>
        <MaterialReactTable
          muiTableContainerProps={{
            sx: {
              "& thead": {
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
              "& thead tr th:first-of-type": {
                borderRight: "1px solid #e0e0e0",
                pr: 3,
                "& > div": {
                  pl: "8px",
                },
              },
              "& tbody tr td:first-of-type": {
                borderRight: "1px solid #e0e0e0",
                pr: 0,
              },
              "& thead tr th:nth-of-type(2)": {
                pl: 3,
              },
              "& tbody tr td:nth-of-type(2)": {
                pl: 3,
              },
              border: "1px solid #D9D9D9",
              borderRadius: "10px",
              minWidth: "100%",
              width: "100%",
              maxWidth: "100%",
              "@media (min-width: 600px)": {
                width: "100%",
              },
              "@media (min-width: 960px)": {
                width: "1100px",
              },
              "@media (min-width: 1280px)": {
                width: "1100px",
              },
              "@media (min-width: 1440px)": {
                width: "1100px",
              },
              "@media (min-width: 1680px)": {
                width: "1300px",
              },
              "@media (min-width: 2400px)": {
                width: "100%",
              },
            },
          }}
          muiTablePaperProps={{
            sx: {
              borderRadius: "10px",
              boxShadow: "none",
            },
          }}
          muiTopToolbarProps={{
            sx: {
              display: "none",
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              display: "none",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              color: COLORS.Mainblue,
              lineHeight: "30px",
              "& .MuiBadge-root": {
                display: "none",
              },
            },
          }}
          muiTableHeadRowProps={{
            sx: {
              height: "45px",
              backgroundColor: "#F8FAFC",
            },
          }}
          muiTableBodyRowProps={{
            sx: {
              height: "45px",
              backgroundColor: "white",
            },
          }}
          columns={dataProvidersColumns}
          data={userRecords}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
          }}
          enableRowActions
          enablePagination={false}
          renderRowActions={({ row }) => (
            <Box
              sx={{
                height: "45px",
                alignItems: "center",
                display: "flex",
                paddingRight: "5px",
              }}
            >
              <Tooltip arrow placement="top" title="Delete">
                <IconButton
                  sx={{ width: "35px", height: "35px" }}
                  color="error"
                  onClick={() => {
                    deleteDataProviderControls.open();
                    setDataProviderId(row.original.id as number)
                  }
                  }
                >
                  <EsgDeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </Box>
      <EstimatesModal
        isOpen={
          addEstimatesModalOpen || EditEstimateOpen || isEstimateModalOpen || editEstimateNotesOpen
        }
        onClose={handleCloseEstimatesModal}
        columns={EstimatesColumns}
        allowedValues={allowedValues}
        site={siteData || ({} as SiteByIdDTO)}
        editEstimates={EditEstimate}
        isEditNotes={isEditNotes}
        onEstimatesDataUpdate={onEstimatesDataUpdate}
      />

      <CollectorModal
        isOpen={
          addCollectorModalOpen || EditCollectorOpen || isCollectorModalOpen
        }
        clickType={clickType}
        onClose={handleCloseCollectorModal}
        columns={generatedCollectorsColumns}
        allowedValues={allowedValues}
        site={siteData || ({} as SiteByIdDTO)}
        editCollector={EditCollector}
        isCollectorEditNotes={isCollectorEditNotes}
        onCollectorDataUpdate={onCollectorDataUpdate}
        collectorData={collectorData}
      />
      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={
          toDeleteSite
            ? onDeleteClick
            : collectorData?.id !== undefined
              ? () => deleteCollector(collectorData?.id)
              : () => deleteEstimate(deleteEstimateId)
        }
        target={
          toDeleteSite ? (
            <SiteNameDetail name={siteData?.name} />
          ) : collectorData?.id !== undefined ? (
            "Collector"
          ) : (
            "Parameter based Estimate"
          )
        }
        title={
          toDeleteSite
            ? "Delete Site?"
            : collectorData?.id !== undefined
              ? "De-activate Utility Provider?"
              : "De-activate Parameter based estimate"
        }
        confirmTitle="De-activate"
      />
      <DeleteDialog
        isOpen={deleteDataProviderControls.isOpen}
        onCancel={deleteDataProviderControls.close}
        onDeleteClick={() => handleDeleteProvider(dataProviderId)}
        target="Data Provider"
        title="Delete Data Provider?"
      />
      <DeleteDialog
        isOpen={sureToDeleteCollectorsModal}
        onCancel={() => setSureToDeleteCollectorsModal(false)}
        onDeleteClick={() => handlerFullyDeleteCollector(currentCollectorToDelete)}
        target="Delete Collector"
        title="Delete Utility Provider?"
      />
      <UserModal
        isOpen={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        users={allowedUsers}
        site={siteData || ({} as SiteByIdDTO)}
      />
      {
        showSideInfoDrawer && (
          <UnitCostInfoDrawer
            unitCostData={UnitCostData}
            historicData={historicData}
            isOpen={showSideInfoDrawer}
            onClose={() => setShowSideInfoDrawer(false)}
          />
        )
      }
    </>
  );
};
